import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

// ELIMINAMOS imports de react-bootstrap para evitar conflictos
// Usamos HTML estándar con clases de Bootstrap CSS

const TabAnalisisProveedores = ({ empresa = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA' }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [openRowIndex, setOpenRowIndex] = useState(null);

  // --- FORMATEADORES ---
  const formatMoney = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
  
  const formatDate = (dateStr) => {
      if(!dateStr) return '-';
      const date = new Date(dateStr);
      // Ajuste de zona horaria
      return new Date(date.getTime() + date.getTimezoneOffset() * 60000).toLocaleDateString('es-AR');
  };

  // --- LÓGICA PARA DETECTAR VENCIMIENTO ---
  const esVencida = (dateStr) => {
      if (!dateStr) return false;
      const fechaVenc = new Date(dateStr);
      // Ajustamos la fecha de vencimiento igual que en el formateo
      const vencimientoAjustado = new Date(fechaVenc.getTime() + fechaVenc.getTimezoneOffset() * 60000);
      
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0); // Quitamos la hora a "hoy" para comparar solo fechas

      return vencimientoAjustado < hoy; // True si la fecha es anterior a hoy
  };

  const toggleRow = (index) => {
      if (openRowIndex === index) {
          setOpenRowIndex(null);
      } else {
          setOpenRowIndex(index);
      }
  };

  // --- 1. CARGA DE DATOS ---
  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: result } = await api.get('/compras/dashboard/analisis-deuda-real', {
          params: { empresa },
          signal: controller.signal,
        });

        if (Array.isArray(result)) {
          setData(result);
          setFilteredData(result);
        } else {
          setData([]);
          setFilteredData([]);
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        console.error(err);
        setError("No se pudo conectar con el servidor SAP.");
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
    // Recarga al cambiar de empresa: la petición anterior se aborta.
  }, [empresa]);

  // --- 2. FILTRO ---
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredData(data.filter(p => 
        (p.CardName && p.CardName.toLowerCase().includes(term)) || 
        (p.CardCode && p.CardCode.toLowerCase().includes(term))
      ));
    }
  }, [searchTerm, data]);

  // --- RENDERIZADO ---
  return (
    <div className="mt-4">
      
      {/* --- BUSCADOR --- */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body p-3 d-flex align-items-center">
            <strong className="me-3">Buscar Proveedor:</strong>
            <input 
                type="text" 
                className="form-control w-50" 
                placeholder="Nombre o Código..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
            />
            <div className="ms-auto text-muted">
                Total Proveedores: <strong>{filteredData.length}</strong>
            </div>
        </div>
      </div>

      {/* --- RESULTADOS --- */}
      {isLoading ? (
        <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-2 text-primary">Calculando composición de deuda real...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger shadow-sm">{error}</div>
      ) : filteredData.length === 0 ? (
          <div className="alert alert-info">No se encontraron deudas pendientes.</div>
      ) : (
        
        /* --- LISTA TIPO ACORDEÓN --- */
        <div className="accordion" id="accordionProveedores">
          {filteredData.map((prov, index) => {
            const isOpen = openRowIndex === index;
            
            return (
                <div className="accordion-item mb-2 shadow-sm border rounded" key={prov.CardCode || index}>
                    
                    {/* HEADER */}
                    <h2 className="accordion-header">
                        <button 
                            className={`accordion-button ${isOpen ? '' : 'collapsed'}`} 
                            type="button" 
                            onClick={() => toggleRow(index)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex w-100 align-items-center justify-content-between pe-3">
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold text-dark me-2">{prov.CardName}</span>
                                    <span className="badge bg-light text-secondary border">{prov.CardCode}</span>
                                </div>
                                <div>
                                    <span className="badge bg-danger p-2 fs-6 shadow-sm">
                                        {formatMoney(prov.TotalDeuda)}
                                    </span>
                                </div>
                            </div>
                        </button>
                    </h2>

                    {/* BODY */}
                    {isOpen && (
                        <div className="accordion-collapse collapse show">
                            <div className="accordion-body bg-light">
                                <div className="row g-3">
                                    
                                    {/* COLUMNA 1: FACTURAS (CON COLUMNA VENCIMIENTO) */}
                                    <div className="col-md-7"> {/* Aumenté un poco el ancho */}
                                        <div className="card h-100 border-primary border-opacity-25">
                                            <div className="card-header bg-primary bg-opacity-10 text-primary fw-bold">
                                                <i className="bi bi-receipt me-2"></i>Facturas Pendientes ({prov.Facturas.length})
                                            </div>
                                            <div className="card-body p-0">
                                                {prov.Facturas.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-striped table-hover mb-0 small">
                                                            <thead>
                                                                <tr>
                                                                    <th>Emisión</th>
                                                                    {/* NUEVA COLUMNA */}
                                                                    <th>Vencimiento</th> 
                                                                    <th>N° Doc</th>
                                                                    <th className="text-end">Saldo</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {prov.Facturas.map(f => {
                                                                    const vencida = esVencida(f.DocDueDate);
                                                                    return (
                                                                        <tr key={f.DocEntry}>
                                                                            <td>{formatDate(f.DocDate)}</td>
                                                                            
                                                                            {/* CELDA VENCIMIENTO CON LÓGICA VISUAL */}
                                                                            <td className={vencida ? "text-danger fw-bold" : ""}>
                                                                                {formatDate(f.DocDueDate)}
                                                                                {vencida && <i className="bi bi-exclamation-circle-fill ms-1" title="Factura Vencida"></i>}
                                                                            </td>
                                                                            
                                                                            <td>{f.DocNum}</td>
                                                                            <td className="text-end fw-bold">{formatMoney(f.SaldoPendiente)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : <p className="text-muted p-3 mb-0 small">No hay facturas.</p>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* COLUMNA 2: ENTREGAS */}
                                    <div className="col-md-5">
                                        <div className="card h-100 border-warning border-opacity-25">
                                            <div className="card-header bg-warning bg-opacity-10 text-dark fw-bold">
                                                <i className="bi bi-box-seam me-2"></i>Remitos a Facturar ({prov.Entregas.length})
                                            </div>
                                            <div className="card-body p-0">
                                                {prov.Entregas.length > 0 ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-striped table-hover mb-0 small">
                                                            <thead>
                                                                <tr>
                                                                    <th>Fecha</th>
                                                                    <th>N° Doc</th>
                                                                    <th>Coment.</th>
                                                                    <th className="text-end">Estimado</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {prov.Entregas.map(e => (
                                                                    <tr key={e.DocEntry}>
                                                                        <td>{formatDate(e.DocDate)}</td>
                                                                        <td>{e.DocNum}</td>
                                                                        <td className="text-truncate" style={{maxWidth: '120px'}} title={e.Comments}>
                                                                            {e.Comments || '-'}
                                                                        </td>
                                                                        <td className="text-end fw-bold text-secondary">{formatMoney(e.SaldoPendiente)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : <p className="text-muted p-3 mb-0 small">No hay entregas.</p>}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TabAnalisisProveedores;
